import { createFileRoute } from "@tanstack/react-router";
import { Layout, CallBanner } from "@/components/site/Layout";
import { FaqDisclosure } from "@/components/site/FaqDisclosure";
import { PhoneCall, CalendarCheck, Brush } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Will you show up consistently?",
    a: "Yes. We pride ourselves on showing up every single day and completing the job in a timely manner. You'll know exactly when to expect us — and you'll see us.",
  },
  {
    q: "How do you handle pricing?",
    a: "We provide fair, honest pricing with zero high-pressure sales tactics. You get a clear quote up front and that's what you pay.",
  },
  {
    q: "What is your experience level?",
    a: "We bring over 40 years of hands-on experience to every project and firmly stand behind our work. Bart has been painting and replacing siding in this area since 1984.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "How It Works & FAQ — The Crew Paint Contractors" },
      { name: "description", content: "How our ASAP response works and answers to the questions Arlington homeowners ask most." },
      { property: "og:title", content: "How It Works & FAQ" },
      { property: "og:description", content: "Our process and answers to common questions." },
      { property: "og:url", content: "https://thecrewpainting.com/faq" },
    ],
    links: [{ rel: "canonical", href: "https://thecrewpainting.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Faq,
});

function Faq() {
  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-6 pt-20 pb-10">
        <p className="text-xs uppercase tracking-widest text-accent font-semibold">How It Works</p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl text-primary">A clear, ASAP response process</h1>
        <p className="mt-5 text-lg text-muted-foreground">
          When a homeowner calls or submits a request, Bart follows up immediately to schedule a transparent quote. No phone tag, no waiting weeks for a callback.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 grid md:grid-cols-3 gap-6 mt-6">
        {[
          { Icon: PhoneCall, title: "1. You reach out", body: "Call 682-454-5997 or submit a request on our contact page. Either works — call is fastest." },
          { Icon: CalendarCheck, title: "2. Bart calls you ASAP", body: "You'll hear back the same day in nearly every case to lock in a time for a clear, honest quote." },
          { Icon: Brush, title: "3. We get to work", body: "Once scheduled, we show up on time, every day, and complete the job in a timely manner." },
        ].map(({ Icon, title, body }) => (
          <div key={title} className="rounded-xl border border-border bg-card p-7">
            <Icon className="h-6 w-6 text-accent" />
            <h2 className="mt-4 font-display text-lg text-primary">{title}</h2>
            <p className="mt-2 text-muted-foreground">{body}</p>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 mt-24">
        <h2 className="font-display text-3xl md:text-4xl text-primary text-center">Frequently asked questions</h2>
        <div className="mt-10 space-y-3">
          {FAQ_ITEMS.map((item) => (
            <FaqDisclosure
              key={item.q}
              question={item.q}
              answer={item.a}
              className="rounded-xl border border-border bg-card px-6"
            />
          ))}
        </div>
      </section>

      <CallBanner text="Still have questions? Call Bart at 682-454-5997 — he'll answer them himself." />
    </Layout>
  );
}
